import { Controller } from "@hotwired/stimulus";

export default class extends Controller<HTMLElement> {
  static targets = ["item", "detail"];

  declare readonly itemTargets: Element[];
  declare readonly hasDetailTarget: boolean;
  declare readonly detailTarget: HTMLElement;

  connect(): void {
    this.clearDetail();
  }

  select(event: Event): void {
    const selectedItem = event.currentTarget;
    if (!(selectedItem instanceof Element)) {
      return;
    }

    const selectedAlready = selectedItem.classList.contains("is-selected");
    this.clearSelection();

    if (selectedAlready) {
      return;
    }

    selectedItem.classList.add("is-selected");
    selectedItem.setAttribute("aria-pressed", "true");

    const label = selectedItem.getAttribute("data-label") || "";
    const valueLabel = selectedItem.getAttribute("data-value-label") || "";
    this.showDetail(label, valueLabel);
  }

  handleKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        this.select(event);
        return;
      case "Escape":
        event.preventDefault();
        this.clearSelection();
        return;
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        this.focusRelativeItem(event.currentTarget, 1);
        return;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        this.focusRelativeItem(event.currentTarget, -1);
        return;
      case "Home":
        event.preventDefault();
        this.focusItem(this.itemTargets[0]);
        return;
      case "End":
        event.preventDefault();
        this.focusItem(this.itemTargets[this.itemTargets.length - 1]);
        return;
    }
  }

  private focusRelativeItem(currentTarget: EventTarget | null, offset: number): void {
    if (!(currentTarget instanceof Element) || this.itemTargets.length === 0) {
      return;
    }

    const currentIndex = this.itemTargets.indexOf(currentTarget);
    if (currentIndex === -1) {
      return;
    }

    const nextIndex = Math.min(
      Math.max(currentIndex + offset, 0),
      this.itemTargets.length - 1,
    );
    this.focusItem(this.itemTargets[nextIndex]);
  }

  private focusItem(item: Element | undefined): void {
    if (item instanceof HTMLElement || item instanceof SVGElement) {
      item.focus();
    }
  }

  private clearSelection(): void {
    this.itemTargets.forEach((item) => {
      item.classList.remove("is-selected");
      item.setAttribute("aria-pressed", "false");
    });
    this.clearDetail();
  }

  private showDetail(label: string, valueLabel: string): void {
    if (!this.hasDetailTarget) {
      return;
    }

    this.detailTarget.classList.add("is-active");
    this.detailTarget.textContent = valueLabel ? `${label}: ${valueLabel}` : label;
  }

  private clearDetail(): void {
    if (!this.hasDetailTarget) {
      return;
    }

    this.detailTarget.classList.remove("is-active");
    this.detailTarget.textContent = this.detailTarget.dataset.emptyLabel || "";
  }
}
